<template>
  <div class="section-content">
    <div class="section-header">
      <div class="section-title">CONSUMPTION :</div>
    </div>

    <div v-if="isLoading" class="loading-state">
      <div class="loading-spinner"></div>
      <span>Loading consumption data...</span>
    </div>
    <div v-else-if="error" class="error-state">
      <div class="error-message">{{ error }}</div>
      <button class="retry-button" @click="fetchReadings">Retry</button>
    </div>
    <div v-else-if="!deviceId" class="no-data-message">
      <div class="info-icon">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      </div>
      <p>No device attached to this metering point.</p>
      <p class="secondary-text">Consumption chart will appear once a device is attached.</p>
    </div>
    <div v-else-if="readings && readings.length > 1" class="chart-container">
      <div class="chart-body" v-if="consumptionData.length > 0">
        <Line :data="chartData" :options="chartOptions" />
      </div>
      <div v-else class="no-consumption-data">
        <p>Unable to calculate consumption from available readings.</p>
      </div>
    </div>
    <div v-else class="no-data-message">
      <div class="info-icon">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      </div>
      <p>No consumption data available.</p>
      <p class="secondary-text">Consumption chart will appear once more reading data is collected.</p>
    </div>
  </div>
</template>

<script>
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler
} from 'chart.js'
import { Line } from 'vue-chartjs'
import { amrRawToM3 } from '@/services/loraWbusHelper'

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Filler
)

export default {
  name: 'MpidDetailsReadingsChart',
  components: {
    Line
  },
  props: {
    deviceId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      readings: [],
      isLoading: false,
      error: ''
    };
  },
  computed: {
    consumptionData() {
      if (!this.readings || this.readings.length < 2) return [];

      const sortedReadings = [...this.readings].sort((a, b) =>
          new Date(a.date_current_reading) - new Date(b.date_current_reading)
      );

      const consumption = [];
      for (let i = 1; i < sortedReadings.length; i++) {
        const currentReading = sortedReadings[i];
        const previousReading = sortedReadings[i - 1];

        const current = this.readingM3Value(currentReading);
        const previous = this.readingM3Value(previousReading);
        const diff = current - previous;

        consumption.push({
          value: Math.max(0, diff),
          timestamp: currentReading.date_current_reading,
          originalIndex: i
        });
      }
      return consumption;
    },

    totalConsumption() {
      if (this.consumptionData.length === 0) return 0;
      return this.consumptionData.reduce((sum, item) => sum + item.value, 0);
    },

    chartData() {
      if (this.consumptionData.length === 0) {
        return {
          labels: [],
          datasets: []
        };
      }

      const labels = this.consumptionData.map(item => this.formatDateLabel(item.timestamp));
      const data = this.consumptionData.map(item => item.value);

      return {
        labels,
        datasets: [
          {
            label: 'Consumption (m³)',
            data,
            borderColor: '#4fc3f7',
            backgroundColor: 'rgba(79, 195, 247, 0.1)',
            tension: 0.3,
            pointRadius: 0,
            pointHoverRadius: 6,
            pointHoverBackgroundColor: '#4fc3f7',
            pointHoverBorderColor: '#ffffff',
            pointHoverBorderWidth: 2,
            fill: true,
            borderWidth: 2
          }
        ]
      };
    },

    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            titleColor: 'white',
            bodyColor: 'white',
            borderColor: 'rgba(255, 255, 255, 0.1)',
            borderWidth: 1,
            cornerRadius: 8,
            displayColors: false,
            callbacks: {
              title: (context) => {
                return this.formatTooltipDate(context[0].label);
              },
              label: (context) => {
                return `${context.parsed.y.toFixed(3)} m³`;
              }
            }
          }
        },
        interaction: {
          intersect: false,
          mode: 'index'
        },
        scales: {
          x: {
            ticks: {
              color: 'rgba(255, 255, 255, 0.6)',
              font: {
                size: 10
              },
              maxTicksLimit: 5
            },
            grid: {
              display: false
            },
            border: {
              display: false
            }
          },
          y: {
            ticks: {
              color: 'rgba(255, 255, 255, 0.6)',
              font: {
                size: 10
              },
              callback: function(value) {
                return value.toFixed(1) + ' m³';
              }
            },
            grid: {
              color: 'rgba(255, 255, 255, 0.1)',
              drawTicks: false
            },
            border: {
              display: false,
              dash: [5, 10]
            }
          }
        }
      };
    }
  },
  watch: {
    deviceId: {
      immediate: true,
      handler(newDeviceId) {
        if (newDeviceId) {
          this.fetchReadings();
        } else {
          this.readings = [];
        }
      }
    }
  },
  methods: {
    readingM3Value(reading) {
      const additionalData = reading?.additional_data || {};
      const rawValue = additionalData.Volume?.Raw ?? reading?.current_reading;
      const converted = amrRawToM3(rawValue, additionalData);

      if (converted !== null) return converted;

      return Number(reading?.current_reading) || 0;
    },

    async fetchReadings() {
      if (!this.deviceId) return;

      this.isLoading = true;
      this.error = '';
      const apiBaseUrl = process.env.VUE_APP_API_URL;

      try {
        const response = await fetch(
            `${apiBaseUrl}/readings/device/${this.deviceId}?limit=100&skip=0`,
            {
              method: 'GET',
              headers: {
                'accept': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`
              }
            }
        );

        if (!response.ok) {
          throw new Error(`Error fetching readings: ${response.statusText}`);
        }

        const data = await response.json();
        this.readings = data.readings || [];
      } catch (err) {
        console.error(err);
        this.error = err.message || 'Failed to load consumption data';
      } finally {
        this.isLoading = false;
      }
    },

    formatDateLabel(dateString) {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      return `${day}/${month}`;
    },

    formatTooltipDate(dateString) {
      if (dateString.includes('/') && dateString.length === 5) {
        const labelIndex = this.chartData.labels.indexOf(dateString);
        if (labelIndex >= 0 && this.consumptionData[labelIndex]) {
          const date = new Date(this.consumptionData[labelIndex].timestamp);
          const day = String(date.getDate()).padStart(2, '0');
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          return `${day}/${month}/${year}`;
        }
      }
      return dateString;
    }
  }
};
</script>

<style scoped>
.section-content {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-header-alt {
  margin-top: 80px;
}

.section-title {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: white;
  display: inline-block;
}

.chart-container {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  padding: 16px 0;
}

.chart-body {
  position: relative;
  flex-grow: 1;
  min-height: 200px;
  max-height: 330px;
  height: 200px;
}

.no-consumption-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  opacity: 0.8;
  text-align: center;
}

.no-consumption-data p {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 15px;
}

.loading-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top: 3px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 15px;
}

.error-message {
  color: #ff8a80;
  text-align: center;
}

.retry-button {
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 6px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.retry-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.no-data-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  opacity: 0.8;
  text-align: center;
}

.info-icon {
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 16px;
}

.no-data-message p {
  font-size: 13px;
  max-width: 80%;
  margin-bottom: 8px;
  color: white;
}

.secondary-text {
  font-size: 11px !important;
  opacity: 0.7;
}

@media (max-width: 767.98px) {
  .section-header {
    margin-top: 60px;
  }

  .section-header-alt {
    margin-top: 60px;
  }
}
</style>
